import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  MessageSquare, Mic, PenTool, Star, ChevronDown, ChevronUp,
  ArrowLeft, Loader2, BookOpen, Info, Sparkles, Lightbulb, Wand2,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import ViewModeToggle, { useViewMode } from '@/components/student/ViewModeToggle';
import { translateFeedbackForStudent } from '@/utils/studentFriendlyTranslator';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODALITY_CONFIG = {
  writing: { icon: PenTool, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', label: 'Writing' },
  speaking: { icon: Mic, color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200', label: 'Speaking' },
};

const ScoreBar = ({ score, max }) => {
  const pct = max > 0 ? (score / max) * 100 : 0;
  const color = pct >= 80 ? 'bg-green-500' : pct >= 50 ? 'bg-yellow-500' : pct >= 25 ? 'bg-orange-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-sm font-bold w-12 text-right">{score}/{max}</span>
    </div>
  );
};

const StudentFeedback = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState({});
  const [filterModality, setFilterModality] = useState('all');
  const [viewMode, setViewMode] = useViewMode('student');
  // v6.36.23 — per-item translation state for the "Translate this
  // feedback" button. Keyed by response_id.
  const [translations, setTranslations] = useState({});
  const [translating, setTranslating] = useState({});

  const translateFeedback = async (responseId) => {
    if (!responseId || translating[responseId] || translations[responseId]) return;
    setTranslating((p) => ({ ...p, [responseId]: true }));
    try {
      // v6.36.24 — Explicit Authorization header + withCredentials.
      // POSTs were 401-ing because the axios global interceptor wasn't
      // always firing on cross-origin POST requests after a demo
      // quick-switch (cookie+Bearer race during AuthProvider remount).
      const token = localStorage.getItem('mosaic_session_token');
      const res = await axios.post(
        `${API}/student/translate-feedback`,
        { response_id: responseId },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true,
        },
      );
      setTranslations((p) => ({ ...p, [responseId]: res.data?.translation || '' }));
    } catch (e) {
      const msg = e.response?.data?.detail || 'Could not translate that feedback right now.';
      toast.error(msg);
    } finally {
      setTranslating((p) => ({ ...p, [responseId]: false }));
    }
  };

  useEffect(() => {
    const fetchFeedback = async () => {
      try {
        const res = await axios.get(`${API}/student/feedback`);
        setData(res.data);
      } catch (e) {
        if (e.response?.status === 401) {
          toast.error('Please log in to view your feedback.');
        } else {
          toast.error('Failed to load feedback.');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchFeedback();
  }, []);

  const toggleItem = (idx) => {
    setExpandedItems(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const filtered = data?.feedback_items?.filter(item =>
    filterModality === 'all' || item.modality === filterModality
  ) || [];

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="student-feedback-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <Link to={user?.role === 'student' ? '/my-assessment' : '/results'}>
          <Button variant="ghost" size="sm" className="h-8 px-2">
            <ArrowLeft size={18} />
          </Button>
        </Link>
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="feedback-title">
          My Feedback
        </h1>
      </div>
      <p className="text-sm text-gray-500 mb-6 ml-11">
        Teacher scores and feedback on your Writing and Speaking responses
      </p>

      {/* v6.36.22 — Student / Teacher View toggle */}
      {data?.feedback_items?.length > 0 && (
        <div className="flex justify-end mb-6">
          <ViewModeToggle mode={viewMode} onChange={setViewMode} />
        </div>
      )}

      {/* Summary */}
      {viewMode === 'teacher' && data?.summary?.total_graded > 0 && (
        <div className="grid grid-cols-2 gap-0 border-t border-l border-gray-300 mb-8" data-testid="feedback-summary">
          <div className="border-b border-r border-gray-300 p-5">
            <div className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {data.summary.total_graded}
            </div>
            <div className="text-xs text-gray-500 mt-1">Responses Scored</div>
          </div>
          <div className="border-b border-r border-gray-300 p-5">
            <div className="text-3xl font-bold text-[#002FA7]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {data.summary.avg_score}
            </div>
            <div className="text-xs text-gray-500 mt-1">Average Score</div>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-2 mb-6" data-testid="feedback-filter">
        <span className="text-sm text-gray-500 mr-2">Filter:</span>
        {['all', 'writing', 'speaking'].map(f => (
          <Button
            key={f}
            variant={filterModality === f ? 'default' : 'outline'}
            size="sm"
            className={`h-8 capitalize ${filterModality === f ? 'bg-[#002FA7] text-white' : 'btn-sharp'}`}
            onClick={() => setFilterModality(f)}
            data-testid={`filter-${f}`}
          >
            {f === 'all' ? 'All' : f}
          </Button>
        ))}
        <span className="ml-auto text-sm text-gray-400">{filtered.length} item{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Empty State */}
      {filtered.length === 0 && (
        <div className="text-center py-16 border-2 border-dashed border-gray-200" data-testid="no-feedback">
          <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <h3 className="font-semibold text-gray-500 mb-1">No Scored Responses Yet</h3>
          <p className="text-sm text-gray-400 mb-4">
            Your teacher hasn't scored any of your Writing or Speaking responses yet. Check back soon!
          </p>
          <Link to={user?.role === 'student' ? '/my-assessment' : '/grades'}>
            <Button className="btn-sharp-primary" data-testid="take-exam-btn">
              <BookOpen size={16} className="mr-2" /> Take a Practice Exam
            </Button>
          </Link>
        </div>
      )}

      {/* Feedback Items */}
      <div className="space-y-3" data-testid="feedback-list">
        {filtered.map((item, idx) => {
          const config = MODALITY_CONFIG[item.modality] || MODALITY_CONFIG.writing;
          const ModalityIcon = config.icon;
          const isExpanded = expandedItems[idx];
          const scorePct = item.max_score > 0 ? Math.round((item.score / item.max_score) * 100) : 0;

          return (
            <div
              key={idx}
              className={`border-2 ${config.border} overflow-hidden transition-all`}
              data-testid={`feedback-item-${idx}`}
            >
              {/* Item Header */}
              <div
                className={`p-4 cursor-pointer hover:bg-gray-50 flex items-center gap-4`}
                onClick={() => toggleItem(idx)}
              >
                <div className={`w-10 h-10 ${config.bg} flex items-center justify-center flex-shrink-0`}>
                  <ModalityIcon size={20} className={config.color} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm">{config.label} Response</span>
                    <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500">Grade {item.grade_level}</span>
                    {item.is_custom && <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700">Custom</span>}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5 truncate">{item.question_text}</p>
                </div>
                <div className="flex items-center gap-4 flex-shrink-0">
                  <div className="text-right">
                    <div className={`text-lg font-bold ${scorePct >= 60 ? 'text-green-600' : scorePct >= 30 ? 'text-orange-600' : 'text-red-600'}`}>
                      {item.score}/{item.max_score}
                    </div>
                    <div className="text-xs text-gray-400">
                      {item.graded_at ? new Date(item.graded_at).toLocaleDateString() : ''}
                    </div>
                  </div>
                  {isExpanded ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
                </div>
              </div>

              {/* Expanded Detail */}
              {isExpanded && (
                <div className="border-t border-gray-200 p-4 space-y-4" data-testid={`feedback-detail-${idx}`}>
                  {/* v6.36.22 — Student-friendly mood + focus card (shown above all technical detail in student view) */}
                  {viewMode === 'student' && (() => {
                    const t = translateFeedbackForStudent({
                      gradeLevel: item.grade_level,
                      modality: item.modality,
                      score: item.score,
                      maxScore: item.max_score,
                    });
                    return (
                      <div className="border-2 border-[#002FA7] p-4 space-y-3" data-testid={`student-friendly-feedback-${idx}`}>
                        <div className="flex items-start gap-2">
                          <Sparkles size={18} className="text-[#002FA7] flex-shrink-0 mt-0.5" />
                          <p className="text-sm font-semibold text-gray-800">{t.mood}</p>
                        </div>
                        {t.focus && (
                          <div className="flex items-start gap-2 pl-6">
                            <Lightbulb size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-gray-700"><span className="font-semibold">Try this next:</span> {t.focus}</p>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Score Bar */}
                  <div>
                    <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Proficiency Score</div>
                    <ScoreBar score={item.score} max={item.max_score} />
                  </div>

                  {/* Question */}
                  <div>
                    <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Question</div>
                    <p className="text-sm text-gray-700">{item.question_text}</p>
                    {item.question_stimulus && (
                      <p className="text-xs text-gray-500 mt-1 italic">{item.question_stimulus}</p>
                    )}
                  </div>

                  {/* Student Response */}
                  <div>
                    <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Your Response</div>
                    <div className="bg-gray-50 border border-gray-200 p-3 text-sm text-gray-700">
                      {item.student_response || <span className="text-gray-400 italic">No text response recorded</span>}
                    </div>
                  </div>

                  {/* Teacher Feedback */}
                  <div className={`${item.teacher_feedback ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'} p-4`}>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        <Star size={14} className={item.teacher_feedback ? 'text-green-600' : 'text-gray-400'} />
                        <span className="text-xs font-semibold uppercase text-gray-600">Teacher Feedback</span>
                      </div>
                      {item.teacher_feedback && !translations[item.response_id] && (
                        <button
                          type="button"
                          onClick={() => translateFeedback(item.response_id)}
                          disabled={translating[item.response_id]}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-[#002FA7] hover:underline disabled:opacity-60 disabled:cursor-wait"
                          data-testid={`translate-feedback-btn-${idx}`}
                        >
                          {translating[item.response_id] ? (
                            <><Loader2 size={12} className="animate-spin" /> Translating…</>
                          ) : (
                            <><Wand2 size={12} /> Translate for me</>
                          )}
                        </button>
                      )}
                    </div>
                    {item.teacher_feedback ? (
                      <p className="text-sm text-gray-800">{item.teacher_feedback}</p>
                    ) : (
                      <p className="text-sm text-gray-400 italic">No written feedback provided. Score: {item.score}/{item.max_score}</p>
                    )}
                  </div>

                  {translations[item.response_id] && (
                    <div className="bg-blue-50 border border-blue-200 p-4" data-testid={`translated-feedback-${idx}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles size={14} className="text-[#002FA7]" />
                        <span className="text-xs font-semibold uppercase text-gray-600">In Your Words</span>
                      </div>
                      <p className="text-sm text-gray-800">{translations[item.response_id]}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StudentFeedback;
